import React, { useEffect, useState } from "react";
import "./EstadoGeneral.css";
import {
  selectEstadoTablasMirror,
  setEstadoTablasMirror,
  patchEstadoTablaMirror,
  setLoadingPageScreen,
} from "../../features/Dashboard/Slices/dashboardSlice";
import { useAppDispatch } from "../../app/store";
import { useSelector } from "react-redux";
import SlIcon from "@shoelace-style/shoelace/dist/react/icon";
import { dashboardAPI } from "../../Api/dashboardAPI";
import { toastError, toastSuccess } from "../../utils/toast";
import {
  updateSincronizacionTabla,
  checkSyncCountTablaMirror,
  checkSyncTablasMirror,
  getEstadoTablasMirror,
  postActualizarCupos,
  getActualizarTablasMirror,
} from "../../features/Dashboard/AsyncThunks/estadoGeneral/getEstadoTablasMirror";
import ModalConfirmation from "../../components/reusable/ModalConfirmation/ModalConfirmation";
import ModalSyncUpsertHash from "../../components/ModalSyncUpsertHash/ModalSyncUpsertHash";
import ModalSelectSyncMode from "../../components/ModalSelectSyncMode/ModalSelectSyncMode";
import SlProgressRing from "@shoelace-style/shoelace/dist/react/progress-ring";
import useAuth from "../../hooks/useAuth";
import Button from "../../components/reusable/Button/Button";
import ProgressBar from "../../components/reusable/ProgressBar/ProgressBar";
import ConfigurableTable, {
  ColumnConfig,
  ActionButton,
} from "../../components/ConfigurableTable/ConfigurableTable";
import {
  DisparoSyncResponse,
  EstadoTablasMirrorResponse,
  ProgresoSyncTablaMirror,
} from "../../interfaces/dashboardTypes";
import { useSyncProgress } from "../../hooks/useSyncProgress";

const TABLAS_MIRROR_UPSERT_HASH = ["MIRROR_PRIORIDADES", "MIRROR_ALUMNOS_PROGRAMAS", "MIRROR_CURSOS", "MIRROR_PLANES_MATERIA", "MIRROR_ALUMNOS_LIBRETAS"];
//const TABLAS_MIRROR_UPSERT_HASH: string[] = [];
const SYNC_POLLING_INTERVAL_MS = 3000;
const SYNC_TIMEOUT_MS = 10 * 60 * 1000;
const SYNC_COMPLETED_HOLD_MS = 2500;

function EstadoGeneral() {
  const dispatch = useAppDispatch();
  const estadoTablasMirror: EstadoTablasMirrorResponse = useSelector(
    selectEstadoTablasMirror,
  );
  const confirmationModalInitialState = {
    show: false,
    title: "",
    dialog: "",
    onConfirm: () => {},
  };
  const [confirmationModal, setConfirmationModal] = useState(
    confirmationModalInitialState,
  );
  const [descriptionBtn, setDescriptionBtn] = useState<number | null>(null);
  const [showUpsertHashModal, setShowUpsertHashModal] = useState(false);
  const [selectSyncModeTabla, setSelectSyncModeTabla] = useState<string>("");
  const { hasPermission } = useAuth();
  const { snapshots, startPolling, stopPolling, clearSnapshot } =
    useSyncProgress({
      pollingIntervalMs: SYNC_POLLING_INTERVAL_MS,
      timeoutMs: SYNC_TIMEOUT_MS,
      completedHoldMs: SYNC_COMPLETED_HOLD_MS,
      onProgresoUpdated: (progreso) => {
        dispatch(
          patchEstadoTablaMirror({
            clave: progreso.clave,
            cantidadRegistros: progreso.cantidadRegistros,
            cantidadRegistrosOracle: progreso.cantidadRegistrosOracle,
            estado: progreso.estado,
            ultimaSincronizacion: progreso.ultimaSincronizacion,
            ultimaRevision: progreso.ultimaRevision,
            ultimaActualizacion: progreso.ultimaActualizacion,
          }),
        );
      },
      onEstadoUpdated: (estado) => {
        dispatch(setEstadoTablasMirror(estado));
      },
      onSettled: (tabla, status) => {
        if (status === "timeout") {
          toastError(`Timeout al sincronizar ${tabla}`);
        } else if (status === "error") {
          toastError(`Falló la sincronización upsert-hash de ${tabla}`);
        }
      },
    });

  const isTablaLoading = (tabla: string): boolean => {
    const s = snapshots[tabla];
    return !!s && (s.status === "preparing" || s.status === "polling");
  };
  const countTablesInicializada = estadoTablasMirror.reduce(
    (acc: number, current: any) => {
      return current.estado === "INICIALIZADA" ? acc + 1 : acc;
    },
    0,
  );
  const countTablesDesactivada = estadoTablasMirror.reduce(
    (acc: number, current: any) => {
      return current.estado === "DESACTIVADA" ? acc + 1 : acc;
    },
    0,
  );
  const porcentajeInicializadas = Math.round(
    (countTablesInicializada * 100) / estadoTablasMirror.length,
  );

  const clearConfirmationModal = () => {
    setConfirmationModal(confirmationModalInitialState);
  };

  const setLoading = (newState: boolean) => {
    dispatch(setLoadingPageScreen(newState));
  };

  /**
   * Vuelve a mostrar la barra si el run disparado en una visita anterior sigue vivo.
   * Devuelve false cuando ya terminó o quedó colgado y hay que recalcular el estado.
   */
  const reanudarProgresoTabla = async (tabla: string): Promise<boolean> => {
    const usarUpsertHash = TABLAS_MIRROR_UPSERT_HASH.includes(tabla);
    const progreso: ProgresoSyncTablaMirror =
      await dashboardAPI.progresoSyncTablaMirror(tabla, null, usarUpsertHash);
    const enCurso =
      !progreso.completado &&
      typeof progreso.msDesdeInicio === "number" &&
      progreso.msDesdeInicio < SYNC_TIMEOUT_MS;
    if (!enCurso) return false;
    startPolling(tabla, {
      usarUpsertHash,
      elapsedOffsetMs: progreso.msDesdeInicio ?? 0,
    });
    return true;
  };

  useEffect(() => {
    const cargarYRevisarInicializando = async () => {
      const result = await dispatch(getEstadoTablasMirror());
      if (!getEstadoTablasMirror.fulfilled.match(result)) return;

      const tablas = (Array.isArray(result.payload)
        ? result.payload
        : []) as EstadoTablasMirrorResponse;
      const pendientes = tablas.filter(
        (t) => t.estado === "INICIALIZANDO" || t.estado === "DESACTUALIZADA",
      );

      for (const tabla of pendientes) {
        try {
          if (
            tabla.estado === "INICIALIZANDO" &&
            (await reanudarProgresoTabla(tabla.clave))
          ) {
            continue;
          }
          // Si la sync terminó pero el estado quedó colgado, consultar contadores
          // Oracle/mirror uno a uno para recalcular el estado.
          await dashboardAPI.checkSyncCountTablaMirror(tabla.clave);
          const estado = await dashboardAPI.estadoTablasMirror();
          dispatch(setEstadoTablasMirror(estado));
        } catch (error) {
          console.log("Error revisando tabla:", tabla.clave, error);
        }
      }
    };
    void cargarYRevisarInicializando();
  }, []);

  const inicializarConfiguracion = async () => {
    setLoading(true);
    try {
      await dashboardAPI.inicializarConfiguracion();
    } catch (error) {
      toastError();
    }
    setLoading(false);
    clearConfirmationModal();
  };

  /* ------------------------ Metodos Acciones botones ------------------------ */

  const buildIndexes = async () => {
    setLoading(true);
    try {
      await dashboardAPI.buildIndexes();
      toastSuccess("Solicitud enviada");
    } catch (error) {
      toastError();
    }
    setLoading(false);
    clearConfirmationModal();
  };

  const syncInfoTablas = async () => {
    setLoading(true);
    try {
      await dashboardAPI.syncInfoTablas();
      toastSuccess("Diccionario de tablas sincronizado con éxito");
    } catch (error) {
      toastError();
    }
    setLoading(false);
    clearConfirmationModal();
  };

  const sincronizarSiguienteTabla = async () => {
    setLoading(true);
    try {
      await dashboardAPI.sincronizarSiguienteTabla();
    } catch (error) {
      toastError();
    }
    setLoading(false);
  };

  const borrarTodasLasTablas = async () => {
    setLoading(true);
    try {
      await dashboardAPI.deleteAllTables();
    } catch (error) {
      toastError();
    }
    setLoading(false);
    clearConfirmationModal();
  };
  const dropTables = async () => {
    setLoading(true);
    try {
      await dashboardAPI.dropAllTables();
    } catch (error) {
      toastError();
    }
    setLoading(false);
    clearConfirmationModal();
  };
  const actualizarSincronizacionCompleta = async () => {
    await dispatch(getActualizarTablasMirror());
  };
  const actualizarTablasMirror = async () => {
    await dispatch(getEstadoTablasMirror());
  };
  const revisarSincronizacionTablasMirror = async () => {
    await dispatch(checkSyncTablasMirror());
  };
  const actualizarCupos = async () => {
    await dispatch(postActualizarCupos());
  };

  const actualizarSincronizacionTabla = async (tabla: string) => {
    setLoading(true);
    try {
      await dispatch(updateSincronizacionTabla(tabla));
      await actualizarTablasMirror();
    } catch (error) {
      toastError();
    }
    setLoading(false);
  };

  const revisarSincronizacionDeTabla = async (tabla: string) => {
    setLoading(true);
    try {
      await dispatch(checkSyncCountTablaMirror(tabla));
      await actualizarTablasMirror();
      // La barra de un run ya terminado (por ejemplo con timeout) deja de aplicar una vez
      // revisada la tabla: se descarta para volver a mostrar el estado. Un run en curso se respeta.
      if (!isTablaLoading(tabla)) {
        clearSnapshot(tabla);
      }
    } catch (error) {
      toastError();
    }
    setLoading(false);
  };

  const populateTables = async () => {
    try {
      setLoading(true);
      const result = await dashboardAPI.inicializarTablasMirror();
      console.log("populateTable result :>> ", result);
      toastSuccess(`Se inicializaron correctamente las tablas`);
      actualizarTablasMirror();
    } catch (error) {
      toastError("Ha ocurrido un error inesperado...");
      console.log("error :>> ", error);
    } finally {
      setLoading(false);
    }
  };

  const resetCronMirror = async () => {
    try {
      setLoading(true);
      const result = await dashboardAPI.resetearTareasCronMirror();
      toastSuccess(`Se resetearon correctamente las tareas`);
      actualizarTablasMirror();
    } catch (error) {
      toastError("Ha ocurrido un error inesperado...");
    } finally {
      setLoading(false);
    }
  };

  const regenerarNovedadesPrioridad = async () => {
    try {
      setLoading(true);
      const result =
        await dashboardAPI.regenerarNovedadesProgramadasPrioridad();
      toastSuccess(`Se regeneraron correctamente las novedades`);
    } catch (error) {
      toastError("Ha ocurrido un error inesperado...");
    } finally {
      setLoading(false);
    }
  };

  const depurarCoreCupos = async () => {
    try {
      setLoading(true);
      const result = await dashboardAPI.depurarCoreCupos();
      clearConfirmationModal();
      toastSuccess(
        `Se depuraron correctamente los cupos, ${JSON.stringify(result)}`,
      );
      revisarSincronizacionDeTabla("CORE_CUPOS");
    } catch (error) {
      toastError("Ha ocurrido un error inesperado...");
    } finally {
      setLoading(false);
    }
  };

  const depurarCoreKVInfoCursoOferta = async () => {
    try {
      setLoading(true);
      const result = await dashboardAPI.depurarCoreKVInfoCursoOferta();
      clearConfirmationModal();
      toastSuccess(
        `Se depuraron correctamente los cupos, ${JSON.stringify(result)}`,
      );
      revisarSincronizacionDeTabla("CORE_KV_INFO_CURSO_OFERTA");
    } catch (error) {
      toastError("Ha ocurrido un error inesperado...");
    } finally {
      setLoading(false);
    }
  };

  /* ------------------------------------ . ----------------------------------- */
  const btnsAccionesGlobales = [
    {
      label: "Build Indexes",
      description: "Actualiza los índices de la base de datos.",
      action: buildIndexes,
      disabled: !hasPermission("estado_gral", "build_indexes"),
    },
    {
      label: "Sync info tablas",
      description: "1. Sincroniza la informacion de todas las tablas.",
      action: syncInfoTablas,
      disabled: !hasPermission("estado_gral", "sync_info_tablas"),
    },
    {
      label: "Reset configuracion",
      description: "2. Sincroniza la informacion de todas las tablas.",
      action: () => {
        setConfirmationModal({
          show: true,
          onConfirm: inicializarConfiguracion,
          title: "Desea resetear la configuración?",
          dialog: "",
        });
      },
      disabled: !hasPermission("estado_gral", "reset_configuracion"),
    },
    {
      label: "Sincronizar Siguiente Tabla",
      description: "3. Sincroniza la informacion de todas las tablas.",
      action: sincronizarSiguienteTabla,
      disabled: !hasPermission("estado_gral", "sincronizar_siguiente_tabla"),
    },
    {
      label: "Borrar toda la información",
      description: "4. Sincroniza la informacion de todas las tablas.",
      action: () => {
        setConfirmationModal({
          show: true,
          onConfirm: borrarTodasLasTablas,
          title: "¿Desea borrar toda la información?",
          dialog: "Se perderán todos los registros",
        });
      },
      disabled: !hasPermission("estado_gral", "borrar_todas_las_tablas"),
    },
    {
      label: "Elimina (DROP) todas las tablas",
      description: "5. Sincroniza la informacion de todas las tablas.",
      action: () => {
        setConfirmationModal({
          show: true,
          onConfirm: dropTables,
          title: "¿Desea borrar todas las tablas?",
          dialog: "Se perderán todos los registros",
        });
      },
      disabled: !hasPermission("estado_gral", "drop_todas_las_tablas"),
    },
    {
      label: "Revisar sincronización",
      description:
        "6. Revisa el estado de sincronización de las tablas mirror.",
      action: revisarSincronizacionTablasMirror,
      disabled: !hasPermission("estado_gral", "revisar_sincronizacion"),
    },
    {
      label: "Corregir sincronización",
      description: "Actualiza las tablas que se encuentren desactualizadas.",
      action: actualizarSincronizacionCompleta,
      disabled: !hasPermission("estado_gral", "corregir_sincronizacion"),
    },
    {
      label: "Refrescar",
      description: "7. Actualiza la información de pantalla.",
      action: actualizarTablasMirror,
      disabled: !hasPermission("estado_gral", "actualizar_tablas_mirror"),
    },
    {
      label: "Sincronizar Cron",
      description: "8. Actualiza todas las tablas relacionadas al alumno.",
      action: populateTables,
      disabled: !hasPermission("estado_gral", "inicializar_tablas_mirror"),
    },
    {
      label: "Resetear Tareas CRON Mirror",
      description: "Resetea las tareas programadas para sincronizar Mirrors",
      action: resetCronMirror,
      disabled: !hasPermission("estado_gral", "inicializar_tablas_mirror"),
    },
    {
      label: "Regenerar Novedades Prioridad",
      description: "Regenera las novedades programadas de prioridad",
      action: regenerarNovedadesPrioridad,
      //disabled: !hasPermission("estado_gral", "regenerar_novedades_prioridad"),
    },
    {
      label: "Sincronizar (Upsert Hash)",
      description:
        "Sincroniza tablas mirror mediante upsert por hash: solo aplica altas, modificaciones y bajas, sin borrar todos los registros.",
      action: () => setShowUpsertHashModal(true),
      disabled: !hasPermission("estado_gral", "inicializar_tablas_mirror"),
    },
  ];

  useEffect(() => {
    console.log("estadoTablasMirror :>> ", estadoTablasMirror);
  }, [estadoTablasMirror]);

  const ejecutarSincronizacionConProgreso = async (
    tabla: string,
    usarUpsertHash: boolean,
    startedMsg: string,
  ) => {
    if (isTablaLoading(tabla)) return;
    try {
      // El disparo responde de inmediato con los marcadores del run; el avance lo sigue el polling.
      const disparo: DisparoSyncResponse =
        await dashboardAPI.dispararSyncTablaMirror(tabla, usarUpsertHash);
      startPolling(tabla, {
        marcadorSincronizacion: disparo?.marcadorSincronizacion ?? null,
        usarUpsertHash,
      });
      toastSuccess(startedMsg);
    } catch (error) {
      toastError("Ha ocurrido un error inesperado...");
      console.log("error :>> ", error);
      stopPolling(tabla);
      clearSnapshot(tabla);
    }
  };

  const populateTable = async (endpoint: string) => {
    await ejecutarSincronizacionConProgreso(
      endpoint,
      false,
      `Sincronización de ${endpoint} iniciada en background`,
    );
  };

  const populateTableUpsertHash = async (tabla: string) => {
    if (isTablaLoading(tabla)) return;
    startPolling(tabla, { usarUpsertHash: true });
    try {
      await dashboardAPI.inicializarTablaMirror(tabla, true);
      toastSuccess(
        `Sincronización upsert-hash de ${tabla} iniciada en background`,
      );
    } catch (error) {
      toastError("Ha ocurrido un error inesperado...");
      stopPolling(tabla);
      clearSnapshot(tabla);
    }
  };
  const handleDesactivada = (clave: string) => {
    populateTable(clave);
    clearConfirmationModal();
  };

  const handleShowDetailInfo = (descIndex: number | null) => {
    setDescriptionBtn(descIndex);
  };

  const renderCustomTable = () => {
    if (!estadoTablasMirror[0]) {
      return <></>;
    }

    // Configuración de columnas
    const columns: ColumnConfig[] = [
      {
        key: "clave",
        title: "Tabla",
        width: "300px",
        align: "left",
        render: (value, item) => {
          const desactivada = item.estado === "DESACTIVADA";
          return (
            <div className="flex items-center">
              <span className={desactivada ? "text-red-500" : ""}>{value}</span>
              {desactivada && (
                <span className="w-2 h-2 bg-red-500 rounded-full inline-block ml-2"></span>
              )}
            </div>
          );
        },
      },
      {
        key: "cantidadRegistrosOracle",
        title: "Registros Oracle",
        width: "120px",
        align: "center",
      },
      {
        key: "cantidadRegistros",
        title: "Registros Obtenidos",
        width: "120px",
        align: "center",
      },
      {
        key: "ultimaSincronizacion",
        title: "Fecha Sincronización",
        width: "120px",
        align: "center",
      },
      {
        key: "ultimaRevision",
        title: "Fecha Revisión",
        width: "120px",
        align: "center",
      },
      {
        key: "ultimaActualizacion",
        title: "Fecha Actualización",
        width: "120px",
        align: "center",
      },
      {
        key: "estado",
        title: "Estado",
        width: "220px",
        align: "center",
        render: (value, item) => {
          const snapshot = snapshots[item.clave];
          if (snapshot) {
            return (
              <ProgressBar
                percent={snapshot.percent}
                status={snapshot.status}
                elapsedMs={snapshot.elapsedMs}
                label={snapshot.label}
                errorMessage={snapshot.syncError}
                cantidadRegistros={snapshot.cantidadRegistros}
                cantidadRegistrosOracle={snapshot.cantidadRegistrosOracle}
              />
            );
          }
          return (
            <span
              className={`rounded-full text-xs align-middle font-medium px-2 py-1 ${
                value === "INICIALIZADA"
                  ? "bg-primary_light text-primary"
                  : value === "DESACTIVADA"
                    ? "bg-red-50 text-red"
                    : value === "DESACTUALIZADA"
                      ? "bg-yellow_light text-yellow-600"
                      : "bg-transparent text-textSecondary"
              }`}
            >
              {value}
            </span>
          );
        },
      },
    ];

    // Configuración de botones de acción
    const actionButtons: ActionButton[] = [
      {
        label: "Sincronizar",
        iconName: "arrow-repeat",
        theme: "primary",
        size: "small",
        onClick: (item) => {
          const soportaUpsertHash = TABLAS_MIRROR_UPSERT_HASH.includes(
            item.clave,
          );
          if (soportaUpsertHash) {
            setSelectSyncModeTabla(item.clave);
            return;
          }
          const noInicializada = item.estado !== "INICIALIZADA";
          if (noInicializada) {
            setConfirmationModal({
              show: true,
              onConfirm: () => handleDesactivada(item.clave),
              title: "¿Seguro desea sincronizar?",
              dialog: "",
            });
          } else {
            populateTable(item.clave);
          }
        },
        disabled: (item) =>
          item.estado === "DESACTIVADA" ||
          isTablaLoading(item.clave) ||
          !hasPermission("estado_gral", "sincronizar_tabla"),
        className: (item: any) =>
          isTablaLoading(item.clave) ? "opacity-50" : "",
      },
      {
        label: "Revisar",
        iconName: "hdd-stack",
        theme: "blue",
        size: "small",
        onClick: (item) => revisarSincronizacionDeTabla(item.clave),
        disabled: (item) =>
          !hasPermission("estado_gral", "revisar_sincronizacion"),
      },
      {
        label: "Depurar",
        iconName: "journal-check",
        theme: "green",
        size: "small",
        onClick: (item) => {
          setConfirmationModal({
            show: true,
            onConfirm:
              item.clave === "CORE_CUPOS"
                ? depurarCoreCupos
                : depurarCoreKVInfoCursoOferta,
            title: `¿Desea depurar la tabla ${item.clave}?`,
            dialog: `Se eliminaran los cupos de ${item.clave} que no se encuentren en la tabla mirror`,
          });
        },
        disabled: (item) =>
          !hasPermission("estado_gral", "actualizar_contador"),
        show: (item) =>
          item.clave === "CORE_CUPOS" ||
          item.clave === "CORE_KV_INFO_CURSO_OFERTA",
      },
    ];

    // Preparar datos con IDs únicos
    const tableData = estadoTablasMirror.map((item, index) => ({
      ...item,
      id: item.clave || index,
    }));

    return (
      <ConfigurableTable
        data={tableData}
        columns={columns}
        actionButtons={actionButtons}
        loading={false}
        striped={true}
        hoverable={true}
        emptyMessage="No hay tablas disponibles"
      />
    );
  };

  return (
    <div className="p-6 grid grid-cols-4 gap-6">
      <ModalConfirmation
        show={confirmationModal.show}
        onClose={() => setConfirmationModal(confirmationModalInitialState)}
        onConfirm={confirmationModal.onConfirm}
        title={confirmationModal.title}
        message={confirmationModal.dialog}
      />

      <ModalSyncUpsertHash
        show={showUpsertHashModal}
        onClose={() => setShowUpsertHashModal(false)}
        onConfirm={populateTableUpsertHash}
        tablas={estadoTablasMirror}
        tablasSoportadas={TABLAS_MIRROR_UPSERT_HASH}
        snapshots={snapshots}
        timeoutMs={SYNC_TIMEOUT_MS}
      />

      <ModalSelectSyncMode
        show={!!selectSyncModeTabla}
        tabla={selectSyncModeTabla}
        onClose={() => setSelectSyncModeTabla("")}
        onConfirmClassic={() => {
          const tabla = selectSyncModeTabla;
          setSelectSyncModeTabla("");
          populateTable(tabla);
        }}
        onConfirmUpsertHash={() => {
          const tabla = selectSyncModeTabla;
          setSelectSyncModeTabla("");
          populateTableUpsertHash(tabla);
        }}
      />

      <div className="bg-white p-6 rounded-lg border-2 border-cardBorder col-span-1">
        <h4 className="font-semibold text-textSecondary mb-3 text-[16px]">
          Tablas inicializadas
        </h4>
        <div className="grid grid-cols-2">
          <SlProgressRing
            className=""
            value={porcentajeInicializadas}
            style={{
              //@ts-ignore
              "--track-width": "11px",
              "--indicator-width": "12px",
              "--track-color": "#ECEEF2",
              "--indicator-color": "#00C4B3",
            }}
          >
            {porcentajeInicializadas}%
          </SlProgressRing>

          <div className="text-right">
            <p className="font-semibold text-lg">
              {countTablesInicializada}{" "}
              <span className="text-textSecondary">
                / {estadoTablasMirror.length}
              </span>
            </p>
            <p className="text-xs text-secondary">
              {countTablesDesactivada} Tablas Desactivadas
            </p>
          </div>
        </div>
      </div>
      {/* /* -------------------------------------------------------------------------- */
      /*                                   BOTONES                                  */
      /* -------------------------------------------------------------------------- */}
      <div className="bg-white p-6 rounded-lg border-2 border-cardBorder col-span-3 flex flex-col">
        <h4 className="font-semibold text-textSecondary mb-3 text-[16px]">
          Acciones globales
        </h4>
        <div className="flex flex-wrap gap-2">
          {btnsAccionesGlobales.map((btn, index) => {
            return (
              <button
                className="text-textPrimary font-bold hover:underline border-2 border-cardBorder px-3 py-1 rounded-lg hover:bg-cardBorder disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={btn.action}
                onMouseEnter={() => handleShowDetailInfo(index)}
                onMouseLeave={() => handleShowDetailInfo(null)}
                disabled={btn.disabled}
              >
                {btn.label}
              </button>
            );
          })}
        </div>
        <div className="flex-grow flex flex-col justify-end">
          <p className="flex items-center gap-2 text-textSecondary">
            <SlIcon name="info-circle"></SlIcon>
            {descriptionBtn === null ? (
              <>Descripción de la acción al pasar sobre el botón.</>
            ) : (
              <>
                <span className="font-semibold uppercase">
                  {btnsAccionesGlobales[descriptionBtn].label}:
                </span>
                {btnsAccionesGlobales[descriptionBtn].description}
              </>
            )}
          </p>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg col-span-4 border-2 border-cardBorder">
        <h4 className="font-semibold text-textSecondary mb-3 text-[16px]">
          Estado general de tablas
        </h4>
        {renderCustomTable()}
      </div>
    </div>
  );
}

export default EstadoGeneral;
